import React, { useState, useEffect } from "react";
import { Compass, SearchCheck, Send, UserCheck, MessageSquare, Sparkles, Lock, ArrowRight, Shield, AlertTriangle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../config/featureFlags";
import JobSearchAgentTool from "./tools/JobSearchAgentTool";
import OpportunityAnalystTool from "./tools/OpportunityAnalystTool";
import OutreachAssistantTool from "./tools/OutreachAssistantTool";
import InterviewCoachTool from "./tools/InterviewCoachTool";
import CareerAdvisorTool from "./tools/CareerAdvisorTool";

export interface ProToolsPanelProps {
  currentUser?: any;
  billingStatus?: any;
  savedBusinesses?: any[];
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  onUpgradeClick?: () => void;
  onExportCsv?: (businesses: any[]) => void;
  onBusinessCrmUpdate?: (businessId: string, stage: string, followUpAt: string | null) => void;
  allBusinesses?: any[];
  onLaunchSearch?: (params: { keyword: string; location: string; radiusMiles: number }) => void;
  theme?: "light" | "dark";
}

export type AIToolId =
  | "job_search_agent"
  | "opportunity_analyst"
  | "outreach_assistant"
  | "interview_coach"
  | "career_advisor";

export default function ProToolsPanel({
  currentUser,
  billingStatus,
  savedBusinesses = [],
  apiFetch,
  onUpgradeClick,
  onBusinessCrmUpdate,
  allBusinesses = [],
  onLaunchSearch,
  theme = "light"
}: ProToolsPanelProps) {
  const [activeTab, setActiveTab] = useState<AIToolId>("job_search_agent");
  const [selectedEmployerForOutreach, setSelectedEmployerForOutreach] = useState<any | null>(null);

  const [providerHealth, setProviderHealth] = useState<{
    status: "healthy" | "degraded" | "outage";
    outageReason?: string | null;
    lastFailureCode?: string | null;
  } | null>(null);

  const fetchProviderHealth = async () => {
    if (!AI_FEATURES_ENABLED) return;
    try {
      const res = await apiFetch("/api/ai/health");
      if (res.ok) {
        const data = await res.json();
        if (data.health) {
          setProviderHealth({
            status: data.health.status,
            outageReason: data.health.outageReason,
            lastFailureCode: data.health.lastFailure?.code
          });
        }
      }
    } catch (err) {
      console.warn("Failed to check AI provider health:", err);
    }
  };

  useEffect(() => {
    fetchProviderHealth();
  }, [activeTab]);

  const handleProviderError = (errData: any) => {
    const code = errData?.code || errData?.providerError?.code || "provider_billing_required";
    setProviderHealth({
      status: "outage",
      outageReason: errData?.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.",
      lastFailureCode: code
    });
  };

  const isDark = theme === "dark";
  const isPro = billingStatus?.plan?.toLowerCase() === "pro" || currentUser?.isPro;

  const handleOpenOutreachForEmployer = (employer: any) => {
    setSelectedEmployerForOutreach(employer);
    setActiveTab("outreach_assistant");
  };

  const toolsList = [
    {
      id: "job_search_agent" as AIToolId,
      name: AI_FEATURES_ENABLED ? "AI Job Search Agent" : "Job Search Agent",
      description: "Optimizes keywords, job titles, and target industries",
      icon: Compass,
      color: isDark ? "text-blue-400" : "text-blue-600",
      bgColor: isDark ? "bg-blue-950/60" : "bg-blue-50"
    },
    {
      id: "opportunity_analyst" as AIToolId,
      name: AI_FEATURES_ENABLED ? "AI Opportunity Analyst" : "Opportunity Analyst",
      description: "Evaluates listing fit, unknown facts, risks & evidence",
      icon: SearchCheck,
      color: isDark ? "text-indigo-400" : "text-indigo-600",
      bgColor: isDark ? "bg-indigo-950/60" : "bg-indigo-50"
    },
    {
      id: "outreach_assistant" as AIToolId,
      name: AI_FEATURES_ENABLED ? "AI Outreach Assistant" : "Outreach Assistant",
      description: "Generates email, phone, form & intro scripts",
      icon: Send,
      color: isDark ? "text-emerald-400" : "text-emerald-600",
      bgColor: isDark ? "bg-emerald-950/60" : "bg-emerald-50"
    },
    {
      id: "interview_coach" as AIToolId,
      name: AI_FEATURES_ENABLED ? "AI Interview Coach" : "Interview Coach",
      description: "Adaptive 1-on-1 practice interview & prep summary",
      icon: UserCheck,
      color: isDark ? "text-amber-400" : "text-amber-600",
      bgColor: isDark ? "bg-amber-950/60" : "bg-amber-50"
    },
    {
      id: "career_advisor" as AIToolId,
      name: AI_FEATURES_ENABLED ? "AI Career Advisor" : "Career Advisor",
      description: "Conversational strategist tracking your search pipeline",
      icon: MessageSquare,
      color: isDark ? "text-cyan-400" : "text-cyan-600",
      bgColor: isDark ? "bg-cyan-950/60" : "bg-cyan-50"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 font-sans">
      {/* Top Banner & Title */}
      <div className={`${isDark ? "bg-slate-900 border-slate-800 text-white shadow-xl" : "bg-white border-slate-200 text-slate-900 shadow-sm"} rounded-3xl p-6 md:p-8 border`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-3 py-1 ${isDark ? "bg-slate-800 border-slate-700 text-slate-300" : "bg-slate-100 border-slate-200 text-slate-700"} text-[10px] font-black uppercase tracking-widest rounded-full border`}>
                {AI_FEATURES_ENABLED ? "Genuinely Adaptive AI" : "Strategic Pro Tools"}
              </span>
              {!isPro && (
                <span className={`px-2.5 py-0.5 ${isDark ? "bg-amber-400/25 text-amber-200 border-amber-300/40" : "bg-amber-500/10 text-amber-800 border-amber-300/60"} border text-[10px] font-extrabold uppercase rounded-full flex items-center gap-1`}>
                  <Lock className="w-3 h-3" /> Pro Only
                </span>
              )}
            </div>
            <h1 className={`text-2xl md:text-3xl font-black tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
              PRO CAREER WORKSPACE
            </h1>
            <p className={`text-xs md:text-sm ${isDark ? "text-slate-400" : "text-slate-600"} max-w-2xl mt-1.5 leading-relaxed`}>
              5 specialized career tools that adapt dynamically to your experience, saved employers, commute preferences, and local opportunities.
            </p>
          </div>

          {!isPro && onUpgradeClick && (
            <button
              type="button"
              onClick={onUpgradeClick}
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs rounded-2xl shadow-lg transition flex items-center gap-2 cursor-pointer shrink-0 active:scale-95"
            >
              <Sparkles className="w-4 h-4 fill-slate-950 text-slate-950" />
              <span>{AI_FEATURES_ENABLED ? "Unlock AI Tools (Pro)" : "Unlock Pro Tools (Pro)"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Workspace Navigation Tabs */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-6 pt-6 border-t ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          {toolsList.map((tool) => {
            const Icon = tool.icon;
            const isActive = activeTab === tool.id;
            return (
              <button
                key={tool.id}
                type="button"
                onClick={() => setActiveTab(tool.id)}
                className={`p-3 rounded-2xl text-left transition flex flex-col justify-between gap-2 border cursor-pointer ${
                  isActive
                    ? "bg-blue-600 text-white border-blue-600 shadow-md"
                    : isDark
                      ? "bg-slate-800/80 border-slate-700/80 text-slate-300 hover:bg-slate-700/80"
                      : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`p-2 rounded-xl ${
                    isActive
                      ? "bg-blue-700 text-white"
                      : isDark
                        ? "bg-slate-700 text-slate-300"
                        : "bg-white border border-slate-200 text-slate-700 shadow-xs"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {!isPro ? (
                    <span className={`p-1 rounded-md ${
                      isActive
                        ? "bg-amber-400/20 text-amber-200 border border-amber-300/40"
                        : isDark
                          ? "bg-amber-400/20 text-amber-300 border border-amber-400/30"
                          : "bg-amber-100 text-amber-800 border border-amber-300"
                    }`}>
                      <Lock className="w-3 h-3" />
                    </span>
                  ) : (
                    isActive && <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  )}
                </div>
                <div>
                  <h3 className={`text-xs font-black leading-tight flex items-center justify-between ${
                    isActive
                      ? "text-white"
                      : isDark
                        ? "text-slate-200"
                        : "text-slate-800"
                  }`}>
                    <span>{tool.name}</span>
                  </h3>
                  <p className={`text-[10px] line-clamp-1 mt-0.5 ${
                    isActive
                      ? "text-blue-100"
                      : isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                  }`}>
                    {tool.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Workspace Maintenance Banner when Circuit Breaker / AI Outage is active */}
      {providerHealth?.status === "outage" && (
        <div className="p-4 md:p-5 bg-red-50 dark:bg-red-950/80 border-2 border-red-500/80 dark:border-red-700 rounded-3xl flex items-start gap-3.5 shadow-md animate-fade-in">
          <div className="p-2.5 bg-red-600 text-white rounded-2xl shrink-0 shadow-xs">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="text-xs text-red-950 dark:text-red-100 space-y-1.5 text-left min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-black text-sm uppercase tracking-wider text-red-900 dark:text-red-200 flex items-center gap-2">
                <span>⚠️ ALL AI TOOLS ARE DOWN</span>
              </p>
              <span className="text-[10px] font-black uppercase px-2.5 py-1 bg-red-600 text-white rounded-md tracking-wider">
                ALL AI TOOLS DOWN
              </span>
            </div>
            <p className="leading-relaxed font-bold text-xs text-red-900 dark:text-red-100">
              All AI tools are temporarily down due to an AI service outage. All buttons, interactive forms, and AI tools are blocked and disabled until service is restored.
            </p>
            {providerHealth.outageReason && (
              <p className="leading-relaxed font-mono text-[11px] opacity-90 whitespace-pre-line bg-red-100/60 dark:bg-red-900/40 p-2 rounded-lg border border-red-200/60 dark:border-red-800/40">
                {providerHealth.outageReason}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Main Tool Content Container */}
      <div className={`min-h-[500px] ${providerHealth?.status === "outage" ? "pointer-events-none opacity-60 select-none" : ""}`}>
        {!isPro ? (
          <div className={`relative rounded-3xl overflow-hidden border shadow-xl p-6 md:p-10 text-center ${
            isDark
              ? "border-slate-800 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950" /* theme-exception */
              : "border-blue-200 bg-gradient-to-b from-blue-50/90 via-white to-blue-50/50"
          }`}>
            {/* Ambient Background Glow */}
            <div className={`absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl pointer-events-none ${isDark ? "bg-blue-500/10" : "bg-blue-500/15"}`} />

            <div className="relative z-10 max-w-2xl mx-auto flex flex-col items-center">
              <div className="w-16 h-16 rounded-3xl bg-gradient-to-tr from-amber-500 via-blue-600 to-indigo-600 p-0.5 shadow-xl mb-5">
                <div className={`w-full h-full rounded-[22px] flex items-center justify-center ${isDark ? "bg-slate-950" : "bg-white"}`}>
                  <Lock className={`w-8 h-8 ${isDark ? "text-amber-400" : "text-amber-500"}`} />
                </div>
              </div>

              <span className={`px-3 py-1 border text-[11px] font-black uppercase tracking-wider rounded-full mb-3 flex items-center gap-1.5 ${
                isDark ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-amber-500/10 text-amber-700 border-amber-500/30"
              }`}>
                <Shield className={`w-3.5 h-3.5 ${isDark ? "text-amber-400" : "text-amber-600"}`} /> Pro Subscription Required
              </span>

              <h2 className={`text-2xl md:text-3xl font-black tracking-tight mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                AI Career Tools are Blocked on Free Plan
              </h2>

              <p className={`text-xs md:text-sm leading-relaxed mb-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                All 5 AI Career Workspace agents are exclusive to Pro subscribers. Upgrade your plan to get full access to automated job search strategy, opportunity risk scoring, customized outreach scripts, interview practice coaching, and conversational search advising.
              </p>

              {/* 5 Tools Feature Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full text-left mb-8">
                {toolsList.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <div key={tool.id} className={`p-3.5 rounded-2xl border shadow-xs flex items-start gap-3 ${
                      isDark ? "bg-slate-800/80 border-slate-700/80" : "bg-white border-blue-100"
                    }`}>
                      <div className={`p-2 rounded-xl shrink-0 ${tool.bgColor} ${tool.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <h4 className={`text-xs font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}>{tool.name}</h4>
                          <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded border flex items-center gap-0.5 shrink-0 ${
                            isDark ? "bg-amber-500/15 text-amber-300 border-amber-400/30" : "bg-amber-500/15 text-amber-700 border-amber-400/30"
                          }`}>
                            <Lock className="w-2.5 h-2.5" /> PRO
                          </span>
                        </div>
                        <p className={`text-[11px] line-clamp-1 mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{tool.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Button */}
              {onUpgradeClick && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg hover:shadow-blue-500/25 transition active:scale-95 cursor-pointer flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4 text-amber-300 animate-pulse fill-amber-300" />
                  <span>Upgrade to Pro to Unlock AI Tools</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Blurred Preview behind Paywall */}
            <div className={`mt-8 pt-8 border-t opacity-20 filter blur-[3px] pointer-events-none select-none aria-disabled ${isDark ? "border-slate-800/80" : "border-blue-100"}`}>
              {activeTab === "job_search_agent" && (
                <JobSearchAgentTool
                  apiFetch={apiFetch}
                  onLaunchSearch={() => {}}
                  userPreferences={{ location: "", skills: [] }}
                  savedEmployers={[]}
                  theme={theme}
                  providerHealth={providerHealth}
                  onProviderError={handleProviderError}
                />
              )}
              {activeTab === "opportunity_analyst" && (
                <OpportunityAnalystTool
                  apiFetch={apiFetch}
                  savedEmployers={[]}
                  userPreferences={{ skills: [], experience: "", location: "" }}
                  theme={theme}
                  providerHealth={providerHealth}
                  onProviderError={handleProviderError}
                />
              )}
              {activeTab === "outreach_assistant" && (
                <OutreachAssistantTool
                  apiFetch={apiFetch}
                  savedEmployers={[]}
                  userPreferences={{ name: "", skills: [], experience: "" }}
                  theme={theme}
                  providerHealth={providerHealth}
                  onProviderError={handleProviderError}
                />
              )}
              {activeTab === "interview_coach" && (
                <InterviewCoachTool
                  apiFetch={apiFetch}
                  savedEmployers={[]}
                  userPreferences={{ experience: "", skills: [] }}
                  theme={theme}
                  providerHealth={providerHealth}
                  onProviderError={handleProviderError}
                />
              )}
              {activeTab === "career_advisor" && (
                <CareerAdvisorTool
                  apiFetch={apiFetch}
                  savedEmployers={[]}
                  userPreferences={{ location: "", skills: [] }}
                  onNavigateToTool={() => {}}
                  theme={theme}
                  providerHealth={providerHealth}
                  onProviderError={handleProviderError}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {activeTab === "job_search_agent" && (
              <JobSearchAgentTool
                apiFetch={apiFetch}
                onLaunchSearch={
                  onLaunchSearch ||
                  ((params) => {
                    alert(`Search launched for: ${params.keyword} in ${params.location}`);
                  })
                }
                userPreferences={{
                  location: currentUser?.location || "",
                  skills: currentUser?.skills || []
                }}
                savedEmployers={savedBusinesses}
                theme={theme}
                providerHealth={providerHealth}
                onProviderError={handleProviderError}
              />
            )}

            {activeTab === "opportunity_analyst" && (
              <OpportunityAnalystTool
                apiFetch={apiFetch}
                savedEmployers={savedBusinesses}
                userPreferences={{
                  skills: currentUser?.skills || [],
                  experience: currentUser?.experience || "",
                  location: currentUser?.location || "Local"
                }}
                onOpenOutreachForEmployer={handleOpenOutreachForEmployer}
                theme={theme}
                providerHealth={providerHealth}
                onProviderError={handleProviderError}
              />
            )}

            {activeTab === "outreach_assistant" && (
              <OutreachAssistantTool
                apiFetch={apiFetch}
                savedEmployers={savedBusinesses}
                userPreferences={{
                  name: currentUser?.name || "Job Candidate",
                  skills: currentUser?.skills || [],
                  experience: currentUser?.experience || ""
                }}
                onBusinessCrmUpdate={onBusinessCrmUpdate}
                preSelectedEmployer={selectedEmployerForOutreach}
                theme={theme}
                providerHealth={providerHealth}
                onProviderError={handleProviderError}
              />
            )}

            {activeTab === "interview_coach" && (
              <InterviewCoachTool
                apiFetch={apiFetch}
                savedEmployers={savedBusinesses}
                userPreferences={{
                  experience: currentUser?.experience || "",
                  skills: currentUser?.skills || []
                }}
                theme={theme}
                providerHealth={providerHealth}
                onProviderError={handleProviderError}
              />
            )}

            {activeTab === "career_advisor" && (
              <CareerAdvisorTool
                apiFetch={apiFetch}
                savedEmployers={savedBusinesses}
                userPreferences={{
                  location: currentUser?.location || "",
                  skills: currentUser?.skills || []
                }}
                onNavigateToTool={(toolId) => {
                  if (
                    toolId === "job_search_agent" ||
                    toolId === "opportunity_analyst" ||
                    toolId === "outreach_assistant" ||
                    toolId === "interview_coach" ||
                    toolId === "career_advisor"
                  ) {
                    setActiveTab(toolId as AIToolId);
                  }
                }}
                theme={theme}
                providerHealth={providerHealth}
                onProviderError={handleProviderError}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
